import "./Modal.scss"
import css from "styles/export.module.scss"

import IconClose from "assets/img/icon-close.svg?react"

import React, { FunctionComponent, useEffect, useState, RefObject, ReactNode, useMemo, useCallback, useRef } from "react"
import FocusLock, { AutoFocusInside } from "react-focus-lock"
import { useUnit } from "effector-react"
import { createPortal } from "react-dom"
import { throttle } from "lodash"
import cx from "classnames"

import { Flex, FlexItem } from "components/Flex"

import Text from "components/Text/Text"
import Spinner from "components/Spinner/Spinner"
import { getZIndex } from "utils/modal"
import modals from "stores/modals"
import DataLossWarning from "components/DataLossWarning/DataLossWarning"
import DataLossWarningModal from "components/DataLossWarning/DataLossWarningModal"
import Button from "components/Button/Button"

type ModalType = "popup" | "sidebar"

export interface IModalProps {
    type?: ModalType
    className?: string
    title?: string
    width?: string
    maxWidth?: string
    fullHeight?: boolean
    onClose?(): void | Promise<void>
    isBusy?: boolean
    closeButton?: boolean
    sizeToContent?: boolean
    toolbar?: ReactNode
    modalBodyContentEl?: RefObject<HTMLDivElement | null>
    modalContentEl?: RefObject<HTMLDivElement | null>
    pictogram?: React.JSX.Element
    dataLossWarning?: boolean
    hidden?: boolean
    modalBodyStyle?: React.CSSProperties
    modalContentStyle?: React.CSSProperties
    footer?: ReactNode | ((close?: (forse?: boolean) => void) => ReactNode)
    withWysiwygEditor?: boolean
    children?: ReactNode
}

let rootModal = document.getElementById("root-modal")
if (!rootModal) {
    rootModal = document.createElement("div")
    rootModal.setAttribute("id", "root-modal")
    document.body.appendChild(rootModal)
}

const Modal: FunctionComponent<React.PropsWithChildren<IModalProps>> = ({
    type = "popup",
    className,
    title = "",
    width,
    maxWidth,
    fullHeight,
    onClose,
    footer,
    children,
    isBusy = false,
    closeButton = true,
    sizeToContent = false,
    toolbar,
    pictogram,
    withWysiwygEditor,
    dataLossWarning,
    hidden,
    modalBodyContentEl,
    modalContentEl,
    modalBodyStyle,
    modalContentStyle,
}) => {
    const [modalClass, setModalClass] = useState("")
    const zIndex = useMemo(() => getZIndex(), [])
    const modalZIndexes = useUnit(modals.zIndexes)
    const [isFocusLockDisabled, setIsFocusLockDisabled] = useState(true)
    const [showDataLossWarning, setShowDataLossWarning] = useState(false)
    const modalBodyRef = useRef<HTMLDivElement | null>(null)
    const [isOverflowedX, setIsOverflowedX] = useState(false)
    const [isOverflowedY, setIsOverflowedY] = useState(false)
    const hasHeader = !!title || closeButton
    const isTopModal = modalZIndexes[modalZIndexes.length - 1] === zIndex

    const throttledCheckIfIsOverflowed = useMemo(() => throttle(() => {
        setIsOverflowedX(
            !!modalBodyRef.current
            && modalBodyRef.current.clientWidth > window.innerWidth)
        setIsOverflowedY(
            !!modalBodyRef.current
            && modalBodyRef.current.clientHeight > window.innerHeight)
    }, 300), [])

    const checkIfIsOverflowed = useCallback(() => {
        throttledCheckIfIsOverflowed()
    }, [throttledCheckIfIsOverflowed])

    useEffect(() => {
        checkIfIsOverflowed()
        window.addEventListener("resize", checkIfIsOverflowed)

        return () => {
            window.removeEventListener("resize", checkIfIsOverflowed)
        }
    }, [checkIfIsOverflowed])

    const handleClose = useCallback((force?: boolean) => {
        if (hidden || typeof onClose !== "function") return
        if (dataLossWarning && !force) {
            setShowDataLossWarning(true)
            return
        }
        modals.removeZIndex(zIndex)
        setModalClass("")

        // wait for css animation finish
        setTimeout(() => {
            onClose()
        }, +css.modalTransitionMs)
    }, [dataLossWarning, hidden, onClose, zIndex])

    const handleKeydown = useCallback((e: KeyboardEvent) => {
        if (e.key !== "Escape" || !isTopModal) return
        handleClose()
    }, [handleClose, isTopModal])

    useEffect(() => {
        document.addEventListener("keydown", handleKeydown)
        return () => document.removeEventListener("keydown", handleKeydown)
    }, [handleKeydown])

    useEffect(() => {
        let transitionTimeoutId: ReturnType<typeof setTimeout> | undefined

        const showTimeoutId = setTimeout(() => {
            if (hidden) {
                setModalClass("")
                setIsFocusLockDisabled(true)
            } else {
                modals.addZIndex(zIndex)
                setModalClass("modal--show")

                // wait for css animation finish
                transitionTimeoutId = setTimeout(() => {
                    setIsFocusLockDisabled(false)
                }, +css.modalTransitionMs)
            }
        })

        return () => {
            clearTimeout(showTimeoutId)
            clearTimeout(transitionTimeoutId)
        }
    }, [zIndex, hidden])

    return createPortal(
        <>
            <FocusLock returnFocus disabled={hidden || withWysiwygEditor || !isTopModal || isFocusLockDisabled}>
                <AutoFocusInside>
                    <div className={cx([
                        'modal',
                        `modal--type-${type}`,
                        className,
                        modalClass,
                        {
                            'modal--hidden': hidden,
                            'modal--size-to-content': sizeToContent,
                            'modal--overflowed-x': isOverflowedX,
                            'modal--overflowed-y': isOverflowedY,
                            'modal--with-wysiwyg-editor': withWysiwygEditor,
                        }
                    ])}
                        style={{ zIndex }}
                        role="dialog"
                        aria-modal="true"
                        onClick={e => e.stopPropagation()}>
                        <div
                            className="modal__overlay"
                            onClick={() => handleClose()}
                            data-testid="modal-overlay"></div>
                        <div className="modal__body"
                            style={{ width, maxWidth: width ? "none" : (maxWidth ? `min(${maxWidth}, calc(100% - 8rem))` : undefined), ...modalBodyStyle }}
                            ref={modalBodyRef}>
                            {!!pictogram &&
                                <div className="ml-3 mt-3">
                                    {pictogram}
                                </div>
                            }
                            <div className={cx([
                                "modal__body-content",
                                {
                                    "modal__body-content--has-header": hasHeader,
                                    "modal__body-content--full-height": fullHeight,
                                }
                            ])} ref={modalBodyContentEl}>
                                {hasHeader &&
                                    <div className="modal__header">
                                        {type === "popup" && !!title &&
                                            <Text type="h2" data-testid="modal-title">{title}</Text>
                                        }
                                        {type === "sidebar" && !!title &&
                                            <h2 className="modal__title" data-testid="sidebar-title">{title}</h2>
                                        }
                                        {!title &&
                                            <div></div>
                                        }
                                        <Flex className="modal__toolbar-wrap"
                                            alignItems="center"
                                            justifyContent="end"
                                            gap="2.4rem">
                                            {!!toolbar &&
                                                <div className="modal__toolbar">{toolbar}</div>
                                            }
                                            {closeButton && typeof onClose === "function" &&
                                                <FlexItem shrink={0}>
                                                    <Button
                                                        onClick={() => handleClose()}
                                                        icon={<IconClose />}
                                                        theme="transparent"
                                                        round
                                                        aria-label="Close"
                                                        data-testid="modal-close"
                                                    />
                                                </FlexItem>
                                            }
                                        </Flex>
                                    </div>
                                }
                                <div className="modal__content"
                                    style={modalContentStyle}
                                    ref={modalContentEl}>
                                    {isBusy &&
                                        <Spinner className="py-6" />
                                    }
                                    {!isBusy && children}
                                </div>
                                {!!footer &&
                                    <div className="modal__footer">
                                        {typeof footer === "function" ? footer(handleClose) : footer}
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </AutoFocusInside>
            </FocusLock>
            {dataLossWarning && !showDataLossWarning &&
                <DataLossWarning enabled={true} />
            }
            {showDataLossWarning &&
                <DataLossWarningModal
                    onClose={() => setShowDataLossWarning(false)}
                    onConfirm={() => {
                        setShowDataLossWarning(false)
                        handleClose(true)
                    }}
                />
            }
        </>,
        rootModal!
    )

}

export default Modal

